import {
  metaEventId,
  normalizeMetaSourcePath,
  parseMetaMoney,
  type MetaTelemetryEventName,
  type MetaTrackingReceipt,
} from "@vetpets/event-contracts"
import { isValidMetaPixelEventId, trackPixelEvent } from "./meta-pixel-runtime"
import { ensureMetaParamBuilderClientReady } from "./meta-param-builder-client"

type MetaTelemetryContent = {
  product_id?: string
  variant_id?: string
  value?: number
  currency?: string
  search_string?: string
}

export type MetaTelemetryInput = {
  event_name: MetaTelemetryEventName
  path: string
  content?: MetaTelemetryContent
}

type MetaTelemetryOptions = {
  browserPixelEnabled?: boolean
}

function safePathname(path: string): string | null {
  const pathname = String(path ?? "").split("?")[0].split("#")[0]
  return normalizeMetaSourcePath(pathname)
}

function normalizedContent(input: MetaTelemetryInput): MetaTelemetryContent | undefined {
  if (input.event_name === "PageView") return undefined
  if (input.event_name === "Search") {
    const searchString = String(input.content?.search_string ?? "").trim().slice(0, 256)
    return searchString ? { search_string: searchString } : undefined
  }

  const variantId = String(input.content?.variant_id ?? "").trim()
  if (!variantId) return undefined
  const productId = String(input.content?.product_id ?? "").trim()
  const value = parseMetaMoney(input.content?.value)
  const currency = String(input.content?.currency ?? "").trim().toUpperCase()
  const hasMoneyPair = value !== undefined && value > 0 && /^[A-Z]{3}$/.test(currency)
  return {
    ...(productId ? { product_id: productId } : {}),
    variant_id: variantId,
    ...(hasMoneyPair ? { value, currency } : {}),
  }
}

function pixelCustomData(eventName: MetaTelemetryEventName, content?: MetaTelemetryContent) {
  if (eventName === "Search") {
    return content?.search_string ? { search_string: content.search_string } : {}
  }
  if (eventName === "ViewContent" && content?.variant_id) {
    return {
      ...(content.currency ? { currency: content.currency } : {}),
      ...(content.value !== undefined ? { value: content.value } : {}),
      content_type: "product",
      content_ids: [content.variant_id],
      contents: [{
        id: content.variant_id,
        quantity: 1,
        ...(content.value !== undefined ? { item_price: content.value } : {}),
      }],
    }
  }
  return {}
}

function dispatchMetaTelemetry(
  input: MetaTelemetryInput,
  options: MetaTelemetryOptions,
  path: string,
  content: MetaTelemetryContent | undefined
): void {
  const event_id = crypto.randomUUID()
  if (options.browserPixelEnabled !== false) {
    trackPixelEvent(input.event_name, pixelCustomData(input.event_name, content), event_id)
  }

  const body = JSON.stringify({
    event_name: input.event_name,
    event_id,
    path,
    ...(content ? { content } : {}),
  })
  void fetch("/api/vetpets/meta/event", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    credentials: "same-origin",
    cache: "no-store",
  }).catch(() => undefined)
}

export function trackMetaTelemetry(
  input: MetaTelemetryInput,
  options: MetaTelemetryOptions = {}
): void {
  const path = safePathname(input.path)
  if (!path) return
  const content = normalizedContent(input)
  if (input.event_name !== "PageView" && !content) return

  void ensureMetaParamBuilderClientReady().then(() => {
    dispatchMetaTelemetry(input, options, path, content)
  })
}

export function trackMetaBusinessReceipt(receipt: MetaTrackingReceipt): void {
  try {
    if (!isValidMetaPixelEventId(receipt.event_name, receipt.event_id)) return
    const value = receipt.custom_data?.value
    if (value !== undefined && (!Number.isFinite(value) || value <= 0)) return
    trackPixelEvent(receipt.event_name, receipt.custom_data ?? {}, receipt.event_id)
  } catch {
    // Browser Pixel is best-effort and must never break a successful commerce action.
  }
}

export function trackMetaPaymentInfoSubmit(
  paymentSessionId: string,
  customData?: MetaTrackingReceipt["custom_data"]
): void {
  let eventId: string
  try {
    eventId = metaEventId("AddPaymentInfo", paymentSessionId)
  } catch {
    return
  }

  trackMetaBusinessReceipt({
    event_name: "AddPaymentInfo",
    event_id: eventId,
    ...(customData ? { custom_data: customData } : {}),
  })

  const body = JSON.stringify({ payment_session_id: paymentSessionId })
  try {
    void fetch("/api/vetpets/meta/payment-info", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      credentials: "same-origin",
      keepalive: true,
      referrerPolicy: "same-origin",
    }).catch(() => undefined)
  } catch {
    // Meta observation is best-effort and must never affect payment submit.
  }
}
